import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';

import {
  BillingCycle,
  ProductType,
  SubscriptionInfoFragment,
  SubscriptionStatus,
  SubscriptionSubstatus,
} from '$/GraphQL/schema/schema';
import { useMe } from '$/hooks/useMe';

import { StripeLoader } from '$/components/StripeLoader';
import { ErrorMessage } from '$/components/ui/MessageText';
import ToggleLeftRight from '$/components/ui/Input/Basics/ToggleLeftRight';

import { PlanTable } from './PlanTable';
import { usePlanPageContext } from '../PlanPage';
import { SubscriptionInfo } from '../SubscriptionInfo';
import { usePlanPageCTAButtons } from '../PlanPageCTAButtons';
import { SwitchingPlanButton } from './SwitchingPlanButton';
import { Per } from '../AddOns/Components';
import { formatCurrency } from '$/utils/formatCurrency';
import Settings from '$/components/ui/Settings';

export const CurrentPlan: React.FC = () => {
  const { team } = useMe();
  const { buttons, error } = usePlanPageCTAButtons();
  const { subscriptionInfo, subscriptionChanges, billingCycle, setBillingCycle } =
    usePlanPageContext();

  const subscribeButton = React.useMemo((): JSX.Element | undefined => {
    switch (team.subscriptionStatus) {
      case SubscriptionStatus.Active:
      case SubscriptionStatus.PastDue:
        return <>{buttons.cancel}</>;
      case SubscriptionStatus.Cancelled:
        return <>{buttons.resubscribe}</>;
      case SubscriptionStatus.Suspended:
        if (team.subscriptionSubstatus === SubscriptionSubstatus.Pending) {
          return undefined;
        }
        return <>{buttons.restore}</>;
      case SubscriptionStatus.Incomplete:
        return <>{buttons.restore}</>;
    }
  }, [
    buttons.cancel,
    buttons.restore,
    buttons.resubscribe,
    team.subscriptionStatus,
    team.subscriptionSubstatus,
  ]);

  const subscriptionInfoWithChanges = React.useMemo(() => {
    const subscriptionInfoFragment: SubscriptionInfoFragment = {
      ...subscriptionInfo.subscriptionInfoFragment,
      products: subscriptionInfo.subscriptionInfoFragment.products.map((product) => ({
        ...product,
        scheduledQuantity: subscriptionChanges[product.type] ?? product.scheduledQuantity,
      })),
    };
    return new SubscriptionInfo(subscriptionInfoFragment, team.id);
  }, [subscriptionInfo, subscriptionChanges, team.id]);

  const [additionalAddOn, addOns] = React.useMemo(() => {
    const additionalAddOn = subscriptionInfo.addOns.find(
      (addOn) => addOn.type === ProductType.AdditionalRegistrants
    );
    const addOns = subscriptionInfo.addOns.filter(
      (addOn) => addOn.type !== ProductType.AdditionalRegistrants
    );
    return [additionalAddOn, addOns];
  }, [subscriptionInfo.addOns]);

  return (
    <>
      <div className='flex items-center justify-between'>
        <Settings.Title className='w-auto font-title text-title font-medium leading-1_75e'>
          Subscription details
        </Settings.Title>
        {subscribeButton}
      </div>
      <Settings.Description>
        We will always ask before upgrading your subscription to a higher level. What's more, we
        will automatically downgrade your subscription at the end of each billing cycle to match the
        number of active webinars you have.{' '}
        <a
          href='https://ewebinar.com/help/how-do-ewebinars-subscription-levels-work'
          target='_blank'
        >
          Learn more
        </a>
      </Settings.Description>
      <div className='w-full overflow-x-auto'>
        <PlanTable
          className='mt-3 w-auto'
          css={css`
            th,
            td {
              line-height: 1.25rem;
              @media screen and (min-width: 920px) {
                line-height: 2rem;
              }
            }
          `}
        >
          <tr
            className={`md:hidden ${subscriptionInfo.subStatus === SubscriptionSubstatus.Pending ? 'hidden' : ''}`}
          >
            <td colSpan={2}>
              <ToggleLeftRight
                css={css`
                  display: flex;
                  justify-content: center;
                  margin-bottom: 0.5rem;
                  input:checked + .switch {
                    ${tw`bg-gray-1`}
                  }
                `}
                value={billingCycle === BillingCycle.Year}
                onChange={(value): void => {
                  setBillingCycle(value ? BillingCycle.Year : BillingCycle.Month);
                }}
                leftLabel={<span className='font-semibold text-lg'>Monthly</span>}
                rightLabel={<span className='font-semibold text-lg'>Annual</span>}
              />
            </td>
          </tr>
          <tr>
            <td></td>
            <td className='price'>
              <span className='font-semibold text-lg'>Monthly</span>
              {subscriptionInfo.billingCycle === BillingCycle.Year &&
              billingCycle === BillingCycle.Month ? (
                <div className='text-sm leading-1e text-coral-1'>
                  {subscriptionInfoWithChanges.saveTotalText}/year more!
                </div>
              ) : subscriptionInfo.billingCycle === BillingCycle.Month ? (
                <div className='text-sm leading-1e text-gray-1'>Renewal amount</div>
              ) : null}
            </td>
            <td className='price'>
              <span className='font-semibold text-lg'>
                Annual <br className='md:hidden' />
                (15% off)
              </span>
              {subscriptionInfo.billingCycle === BillingCycle.Year ? (
                <div className='text-sm leading-1e text-gray-1'>Renewal amount</div>
              ) : subscriptionInfo.currentPeriodBillingCycle === BillingCycle.Year ? (
                <div className='text-sm italic leading-1e text-gray-1'>Switch back to annual</div>
              ) : (
                <div className='text-sm leading-1e text-green-500 font-semibold'>
                  Save {subscriptionInfoWithChanges.saveTotalText}/year!
                </div>
              )}
            </td>
          </tr>
          {subscriptionInfo.nextPriceTier.getSummary(
            subscriptionInfo.product.nextQuantity,
            team.subscriptionStatus === SubscriptionStatus.Cancelled
              ? subscriptionInfo.nextPaymentDueDate?.format('P')
              : undefined
          )}
          {additionalAddOn && <additionalAddOn.ListComponent />}
          {addOns.length > 0 && (
            <tr>
              <td className='font-semibold text-lg pt-2e'>Subscription add-ons</td>
              <td></td>
              <td></td>
            </tr>
          )}
          {addOns.map((AddOn) => (
            <AddOn.ListComponent key={AddOn.type} />
          ))}
          <tr
            css={css`
              td {
                padding: 1rem 0.5rem !important;
                border-radius: 0 0 0.5rem 0.5rem !important;
              }
            `}
          >
            <td className='font-bold align-bottom pt-2e text-lg'>Total subscription rate</td>
            <td className='price font-semibold align-bottom'>
              <span>
                {formatCurrency(subscriptionInfoWithChanges.nextPrices.monthlyTotal)}
                <Per />
              </span>
            </td>
            <td className='price align-bottom'>
              <div className='text-sm text-gray-1 whitespace-no-wrap leading-1e'>
                {formatCurrency(subscriptionInfoWithChanges.nextPrices.annualTotalByMonth)}
                <Per /> x 12
                <span className='text-xs'> months =</span>
              </div>
              <span className='font-semibold'>
                {formatCurrency(subscriptionInfoWithChanges.nextPrices.annualTotal)}
                <Per billingCycle={BillingCycle.Year} />
              </span>
            </td>
          </tr>
          <StripeLoader>
            {subscriptionInfo.billingCycle !== billingCycle ? (
              <tr>
                <td className='hidden md:table-cell'></td>
                <td className='bg-transparent opacity-100 pt-1 table-cell' colSpan={2}>
                  <SwitchingPlanButton />
                </td>
              </tr>
            ) : (
              <tr id='current-plan-buttons'></tr>
            )}
          </StripeLoader>
          {error && (
            <ErrorMessage
              className='mt-1e'
              css={css`
                justify-content: flex-start !important;
              `}
              error={error}
            />
          )}
        </PlanTable>
        {!subscriptionInfo.isInTrial &&
          !subscriptionInfo.isSuspended &&
          subscriptionInfo.billingCycle === BillingCycle.Month &&
          subscriptionInfo.currentPeriodBillingCycle === BillingCycle.Year && (
            <div className='mt-2 text-coral-1 text-md text-center italic'>
              You will not begin to be charged monthly until your next billing date when your annual
              subscription ends.
            </div>
          )}
      </div>
    </>
  );
};
